/**
 * QuoteDetailRoute — Phase 1 Opportunity Decomposition
 * ────────────────────────────────────────────────────
 * Stage router for /opportunities/:id. Opportunity is NOT a canonical surface;
 * this route delegates to the right workspace based on lifecycle stage.
 *
 * Routing matrix:
 *   • Pre-sale (Lead, Appt Scheduled/Completed, Follow-Up Needed)
 *       → render <AppointmentFlow /> as the consultation workspace.
 *   • Closed Won + valid linkedJobId + outcome != rescission/canceled/finance_declined
 *       → redirect to /jobs/:linkedJobId (canonical job workspace).
 *   • Closed Won without valid job (rescission / canceled / finance_declined / no link)
 *       → render <OpportunityReadOnlySummary /> (audit snapshot).
 *   • Closed Lost
 *       → render <OpportunityReadOnlySummary /> (audit snapshot).
 *
 * MUST NOT compute pricing, resolve reps, or read job financials here.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUISkin } from "@/data/UISkinContext";
import { useAppData } from "@/data/AppDataContext";
import { CockpitFrame } from "@/components/cockpit/CockpitFrame";
import { OpportunityReadOnlySummary } from "@/components/OpportunityReadOnlySummary";
import { AppointmentFlow } from "@/components/AppointmentFlow";
import { QuoteService } from "@/services/quote.service";
import { toUiQuote, toDbQuoteUpdate } from "@/types/mappers/quote.mapper";
import QuoteDetail from "@/pages/QuoteDetail";
import type { Opportunity } from "@/data/opportunityTypes";
import type { DbQuote } from "@/types/database";
import type { Quote } from "@/data/quoteTypes";

type NoJobReason = "rescission" | "canceled" | "finance_declined" | "no_link";

export default function QuoteDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const { skin } = useUISkin();
  const { quotes, jobs } = useAppData();
  const navigate = useNavigate();
  const [fetchedOpportunity, setFetchedOpportunity] = useState<Opportunity | null>(null);
  const [fetchedOpportunityId, setFetchedOpportunityId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const opportunityFromContext = quotes.find((q) => q.id === id);

  useEffect(() => {
    let cancelled = false;

    if (!id) {
      setFetchedOpportunity(null);
      setFetchedOpportunityId(null);
      setIsResolving(false);
      return () => { cancelled = true; };
    }

    setIsResolving(!opportunityFromContext);
    void (async () => {
      const { data, error } = await QuoteService.getById(id);
      if (cancelled) return;

      if (error || !data) {
        setIsResolving(false);
        return;
      }

      setFetchedOpportunity(toUiQuote(data as DbQuote) as Opportunity);
      setFetchedOpportunityId(id);
      setIsResolving(false);
    })();

    return () => { cancelled = true; };
  }, [id, opportunityFromContext]);

  const opportunity = useMemo(() => {
    if (fetchedOpportunityId === id) return fetchedOpportunity;
    if (opportunityFromContext) return opportunityFromContext;
    return null;
  }, [opportunityFromContext, fetchedOpportunity, fetchedOpportunityId, id]);

  // Closed Won → either redirect to job or show read-only summary.
  const isWon = opportunity?.stage === "Closed Won";
  const linkedJob = isWon && opportunity?.linkedJobId
    ? jobs.find((j) => j.id === opportunity.linkedJobId || j.jobNumber === opportunity.linkedJobId)
    : undefined;

  // wonOutcome === "net" is the only outcome with a live, valid job.
  // pending → not yet classified; canceled / finance_declined → no live job.
  // (Rescission is a time window on the Job; if rescinded a downstream
  //  process flips wonOutcome to "canceled". No separate flag needed here.)
  const outcome = opportunity?.wonOutcome;
  const isInvalidatedOutcome =
    outcome === "canceled" || outcome === "finance_declined";

  let noJobReason: NoJobReason | undefined;
  if (isWon) {
    if (outcome === "canceled") noJobReason = "canceled";
    else if (outcome === "finance_declined") noJobReason = "finance_declined";
    else if (!linkedJob) noJobReason = "no_link";
  }

  const shouldRedirectToJob =
    isWon && !!linkedJob && !isInvalidatedOutcome;

  useEffect(() => {
    if (shouldRedirectToJob && linkedJob) {
      navigate(`/jobs/${linkedJob.id}`, { replace: true });
    }
  }, [shouldRedirectToJob, linkedJob, navigate]);

  if (!opportunity && isResolving) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Loading opportunity...
      </div>
    );
  }

  if (!opportunity) {
    return <div className="p-8 text-center text-muted-foreground">Opportunity not found.</div>;
  }

  // Closed Won → redirecting; render nothing while navigation happens.
  if (shouldRedirectToJob) return null;

  // Closed Won without valid job, or Closed Lost → read-only summary.
  if (opportunity.stage === "Closed Lost" || (isWon && noJobReason)) {
    return <OpportunityReadOnlySummary opportunity={opportunity} noJobReason={noJobReason} />;
  }

  const handleCloseConsultation = () => {
    navigate("/opportunities", { replace: true });
  };

  const handlePersistUpdate = async (quoteId: string, updates: Partial<Opportunity>) => {
    const { error } = await QuoteService.update(quoteId, toDbQuoteUpdate(updates as Partial<Quote>));
    if (error) {
      console.error("[QuoteDetailRoute] QuoteService.update failed:", error);
      throw error;
    }
  };

  // Pre-sale stages with active config/pricing workspace → QuoteDetail (DB-wired)
  // Covers the full pre-sale arc: Scheduled → Completed → Follow-Up, so stage
  // transitions inside QuoteDetail stay in QuoteDetail without re-routing.
  const isActiveSalesStage =
    opportunity.stage === "Appointment Scheduled" ||
    opportunity.stage === "Appointment Completed" ||
    opportunity.stage === "Follow-Up Needed";

  if (isActiveSalesStage) {
    return (
      <QuoteDetail initialQuote={opportunity as any} onPersistUpdate={handlePersistUpdate} />
    );
  }

  // Lead-only stage → AppointmentFlow
  if (skin === "cockpit") {
    return (
      <CockpitFrame eyebrow="Sales · Opportunity" title="Opportunity Detail">
        <AppointmentFlow
          lead={opportunity}
          open={true}
          onClose={handleCloseConsultation}
        />
      </CockpitFrame>
    );
  }
  return (
    <AppointmentFlow
      lead={opportunity}
      open={true}
      onClose={handleCloseConsultation}
    />
  );
}
